import { ComponentType, ReactNode, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { Button } from 'src/libraries/design/components/button/src/button';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useUserContext } from '@/components/user-context/user-context-provider';
import { Picture } from '@/components/wpl/image/src/image';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { AvatarFacepile } from '@/patterns/return-to-product-modal/src/avatar-facepile';
import { useRecentProductSite } from '@/templates/software/jira/logged-in/utils/use-recent-product-site';
import { useLocation } from '@/utils/location-provider';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import {
	DEFAULT_LOGGED_IN_HERO_PRODUCT,
	LoggedInHeroProduct,
	PRODUCT_CONFIG,
	resolveProductFromLocation,
} from './product-config';

type TRACKING_EVENT_NAMES = (typeof TRACKING_EVENT_NAMES)[keyof typeof TRACKING_EVENT_NAMES];

const TRACKING_EVENT_NAMES = {
	loggedInHeroProductCtaClicked: 'loggedInHeroProductCtaClicked',
	loggedInHeroAnotherSiteCtaClicked: 'loggedInHeroAnotherSiteCtaClicked',
	loggedInHeroExploreFeaturesClicked: 'loggedInHeroExploreFeaturesClicked',
	loggedInHeroCreateNewSiteClicked: 'loggedInHeroCreateNewSiteClicked',
};

/**
 * Builds the analytics payload for a given product. The JSM values are
 * preserved exactly to avoid breaking existing dashboards; other products use
 * their own `actionSubjectId` / `source`.
 */
export const getLoggedInHeroAnalyticsPayload = (product: LoggedInHeroProduct) => ({
	eventType: 'ui',
	action: 'clicked',
	actionSubject: 'button',
	actionSubjectId: PRODUCT_CONFIG[product].analyticsActionSubjectId,
	source: PRODUCT_CONFIG[product].analyticsSource,
});

/**
 * @deprecated Prefer {@link getLoggedInHeroAnalyticsPayload}. Retained for
 * backwards compatibility with existing JSM imports.
 */
export const loggedInHeroAnalyticsPayload = getLoggedInHeroAnalyticsPayload('jsm');

const jsmLoggedInCss = css({
	position: 'relative',
	background: wacToken('color.extended.blue100'),
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	display: 'grid',
	gridTemplateColumns: '1fr',
	placeItems: 'center',
	paddingInline: '100px',
	paddingBlockStart: '50px',
	paddingBlockEnd: '100px',
	width: '100%',
	[media.below.md]: {
		paddingInline: '25px',
	},
});

const heroSectionCss = css({
	position: 'relative',
	color: wacToken('color.primary.black'),
	width: '100%',
	maxWidth: '928px',
	padding: '20px',
	[media.below.sm]: {
		paddingInline: '0px',
	},
});

const anchorContainer = css({
	position: 'relative',
	flexGrow: '1',
	alignSelf: 'stretch',
});

const jsmSiteBoxCss = css({
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '4px',
	width: '100%',
	minWidth: '300px',
	display: 'flex',
	justifyContent: 'space-between',
	flexDirection: 'row',
	alignItems: 'center',
	border: `1px solid ${wacToken('color.primary.black')}`,
	paddingBlock: '16px',
	paddingInline: '16px',
	marginBlock: '20px',
	position: 'relative',
	// On narrow (xs / mobile) screens the 64px site icon + name and the large
	// CTA cannot sit side-by-side, so stack them vertically and let the box
	// shrink to the viewport instead of forcing a 300px minimum (which caused
	// horizontal overflow).
	[media.below.sm]: {
		flexDirection: 'column',
		alignItems: 'stretch',
		minWidth: '0',
		gap: token('space.200'),
	},
});

const goToProductButtonWrapperCss = css({
	// Once the box stacks on xs, give the CTA the full box width so it can
	// stretch edge-to-edge (matching the mobile design).
	[media.below.sm]: {
		display: 'flex',
		width: '100%',
	},
});

// On xs the "Go to <product>" CTA stretches to fill the stacked box. The WAC
// Button is `inline-flex` with a built-in `maxWidth`, so we override both via
// its `css` prop (the canonical way callers restyle this Button — see
// return-to-product-modal-cta.tsx).
const goToProductButtonCss = css({
	[media.below.sm]: {
		width: '100%',
		maxWidth: 'none',
	},
});

const headingCss = css({
	fontSize: '48px',
	lineHeight: '56px',
	fontWeight: '800',
	fontFamily: wacToken('font.charlie.display'),
	marginBlockEnd: '15px',
	paddingInlineStart: '0',
	[media.below.md]: {
		fontSize: '35px',
		lineHeight: '40px',
	},
});

const starCss = css({
	transform: 'rotate(10deg) scale(-1, 1)',
	position: 'absolute',
	display: 'inline-block',
	insetBlockEnd: '-13px',
	insetInlineStart: '892px',
	[media.below.md]: {
		display: 'none',
	},
});

const differentSiteCss = css({
	fontFamily: wacToken('font.charlie.text'),
	marginBlockStart: '15px',
	fontSize: '14px',
	fontWeight: '400',
	[media.below.md]: {
		textAlign: 'center',
	},
});

const subHeadingContainerCss = css({
	display: 'flex',
	flexWrap: 'wrap',
	columnGap: '100px',
	justifyContent: 'space-between',
	alignItems: 'baseline',
});

const createNewSiteAnchorCss = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	fontWeight: '600',
	lineHeight: '28px',
	color: wacToken('color.extended.blue700'),
});

const subHeadingCss = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '20px',
	lineHeight: '28px',
	fontWeight: '400',
});

const nameUnderlineCss = css({
	fontStyle: 'normal',
	position: 'relative',
	[':after']: {
		backgroundImage: `url("data:image/svg+xml,%3Csvg width='69' height='13' viewBox='0 0 69 13' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='Gestural Line/star 5'%3E%3Cpath id='Vector' d='M68.9943 6.17916C57.1037 8.38955 48.3305 2.73368 36.3215 7.37322C35.7609 5.38615 34.3116 2.89774 32.1422 2.86128C33.9516 2.90685 36.8456 8.09785 33.9379 4.62047C32.2288 1.97255 24.7591 5.20844 30.4833 2.9069C26.5319 3.39455 22.9087 6.61668 20.238 9.48335C15.7944 13.5669 19.167 5.66417 15.954 1.69002C11.401 -3.83369 -2.41738 10.5316 0.367259 10.5407C2.14013 8.74051 4.51915 7.61935 5.50357 5.62316C22.8358 -6.19447 10.33 15.677 20.6528 12.0128C37.5155 -3.17739 28.993 10.5726 38.8919 9.63373C44.6617 4.12826 69.1948 12.7602 68.9988 6.17916H68.9943Z' fill='%23FCA700'/%3E%3C/g%3E%3C/svg%3E%0A")`,
		backgroundRepeat: 'no-repeat',
		color: wacToken('color.extended.saffron400'),
		backgroundSize: 'contain',
		content: "''",
		height: '20px',
		insetBlockEnd: -14,
		insetInlineEnd: -2,
		insetInlineStart: -4,
		position: 'absolute',
		[media.only.md]: {
			insetBlockEnd: -10,
		},
		[media.above.lg]: {
			insetBlockEnd: -6,
		},
	},
});

const inlineJSMLogoCss = css({
	display: 'inline-block',
	transform: 'scale(0.5)',
	position: 'relative',
	insetBlockStart: '14px',
	marginInlineStart: '-10px',
	marginInlineEnd: '-5px',
});

const siteInfoContainerCss = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	height: '64px',
	gap: token('space.200'),
});

const sitenameIconCss = css({
	background: wacToken('color.extended.lime300'),
	borderRadius: token('space.050'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '64px',
	width: '64px',
	minWidth: '64px',
});

const sitenameIconTextCss = css({
	color: wacToken('color.extended.lime800'),
	fontFamily: wacToken('font.charlie.display'),
	fontSize: token('space.300'),
	fontWeight: 600,
	lineHeight: '28px',
	letterSpacing: '0.3px',
	textAlign: 'center',
});

const sitenameAndAvatarContainerCss = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	gap: '10px',
	position: 'relative',
	height: '64px',
});

const siteNameSpanCss = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '20px',
	fontWeight: '700',
	height: '20px',
	lineHeight: '1rem',
	textAlign: 'left',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	display: 'inline-block',
	verticalAlign: 'top',
	[media.only.xs]: {
		maxWidth: '200px',
	},
	[media.below.xs]: {
		maxWidth: '90px',
	},
});

const tooltipWrapperCss = css({
	height: '20px',
});

const avatarContainerCss = css({
	height: '24px',
	[media.only.xs]: {
		maxWidth: '200px',
	},
	[media.below.xs]: {
		maxWidth: '90px',
	},
});

const linkPlaceholderCss = css({
	height: '22px',
});

/**
 * Logged-In Hero Section
 *
 * This component only renders for users who are logged in and have at least one
 * site for the detected product. For all other users (logged-out or logged-in
 * without the product), it returns null.
 *
 * The product is detected entirely from the current URL via each product's
 * `pathMatchers` (see {@link resolveProductFromLocation}), falling back to
 * {@link DEFAULT_LOGGED_IN_HERO_PRODUCT}. There is intentionally no `product`
 * prop — a single registration serves every product landing page.
 *
 * On the Uniform preview route the browser pathname is `/uniform-preview`, so
 * the intended page path arrives as a `path` query param instead;
 * {@link resolveProductFromLocation} honours it so each product variant can be
 * previewed locally before shipping.
 */
const LoggedInHeroSection = () => {
	const location = useLocation();

	const product = resolveProductFromLocation(location) ?? DEFAULT_LOGGED_IN_HERO_PRODUCT;

	const loggedInHasProduct = PRODUCT_CONFIG[product].useHasProduct();

	// Don't render while profile is loading or if user doesn't have the product
	if (!loggedInHasProduct) {
		return null;
	}

	return <LoggedInHeroContent product={product} />;
};

const LoggedInHeroContent = ({ product }: { product: LoggedInHeroProduct }) => {
	const config = PRODUCT_CONFIG[product];
	const loggedInUserProfile = useLoggedInUser();
	const { user } = useUserContext();

	// Try to get name from props, and fallback to hook if needed
	const userFirstName = user.name?.split(' ')[0] ?? loggedInUserProfile.name?.split(' ')[0];

	// Try to get picture from props, and fallback to hook if needed
	const userPicture = user?.picture ?? loggedInUserProfile.picture;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const localizedTryLinkWithoutIntent = useLocalizedHref(config.createNewSiteHref);
	const localizedTryLink = config.useResolvedCreateHref(localizedTryLinkWithoutIntent);

	const [hasMultipleSites, setHasMultipleSites] = useState(false);
	const [siteName, setSiteName] = useState('');
	const [siteInitials, setSiteInitials] = useState('');
	const [siteUrl, setSiteUrl] = useState('');
	const {
		hasMultipleSites: mostRecentHasMultipleSites,
		siteName: mostRecentSiteName,
		siteUrl: mostRecentSiteUrl,
		siteInitials: mostRecentSiteInitials,
	} = useRecentProductSite({
		productKeyFilter: config.productKeyFilter,
		activityClientProduct: config.activityClientProduct,
		siteLogin: config.siteLogin,
	});

	useEffect(() => {
		setHasMultipleSites(mostRecentHasMultipleSites);
		setSiteName(mostRecentSiteName);
		setSiteUrl(mostRecentSiteUrl);
		setSiteInitials(mostRecentSiteInitials);
	}, [mostRecentHasMultipleSites, mostRecentSiteInitials, mostRecentSiteName, mostRecentSiteUrl]);

	const analyticsPayload = getLoggedInHeroAnalyticsPayload(product);

	const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
		createAnalyticsEvent({
			...analyticsPayload,
			attributes: {
				eventName,
				url: window.location.href,
				atlasId: 'ATLAS-117392',
			},
		}).fire();
	};

	return (
		<LoggedInHeroSectionView
			productName={config.messages.productName}
			goToProductMessage={config.messages.goToProduct}
			LogoMark={config.LogoMark}
			userFirstName={userFirstName}
			userPicture={userPicture}
			siteName={siteName}
			siteInitials={siteInitials}
			siteUrl={siteUrl}
			createNewSiteHref={localizedTryLink}
			existingSitesHref={config.existingSitesHref}
			hasMultipleSites={hasMultipleSites}
			onCreateNewSiteClick={() =>
				clickHandler(TRACKING_EVENT_NAMES.loggedInHeroCreateNewSiteClicked)
			}
			onGoToProductClick={() => clickHandler(TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked)}
			onDifferentSiteClick={() =>
				clickHandler(TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked)
			}
		/>
	);
};

export interface LoggedInHeroSectionViewProps {
	/** Display name of the product, e.g. "Confluence". */
	productName: string;
	/** Localised message for the primary "Go to <product>" CTA. */
	goToProductMessage: MessageDescriptor;
	/** Brand mark rendered inline in the subheading. */
	LogoMark: ComponentType;
	/** First name of the logged-in user (optional — falls back to "Welcome back"). */
	userFirstName?: string;
	/** Avatar URL of the logged-in user (optional). */
	userPicture?: string;
	/** Display name of the most-recent site. */
	siteName: string;
	/** Initials shown in the site tile. */
	siteInitials: string;
	/** URL the "Go to <product>" CTA links to. */
	siteUrl: string;
	/** URL for the "Create a new workspace" link. */
	createNewSiteHref: string;
	/** URL for the "Looking for a different workspace?" link. */
	existingSitesHref: string;
	/** Whether the user has more than one site (shows the "different workspace" link). */
	hasMultipleSites: boolean;
	onCreateNewSiteClick?: () => void;
	onGoToProductClick?: () => void;
	onDifferentSiteClick?: () => void;
}

/**
 * Pure, presentational view for the logged-in hero. All data is passed in as
 * props (no hooks / providers), so it can be rendered directly in Storybook and
 * unit tests. The hook-driven {@link LoggedInHeroContent} resolves the data and
 * delegates rendering here.
 */
export const LoggedInHeroSectionView = ({
	productName,
	goToProductMessage,
	LogoMark,
	userFirstName,
	userPicture,
	siteName,
	siteInitials,
	siteUrl,
	createNewSiteHref,
	existingSitesHref,
	hasMultipleSites,
	onCreateNewSiteClick,
	onGoToProductClick,
	onDifferentSiteClick,
}: LoggedInHeroSectionViewProps) => {
	const intl = useIntl();

	return (
		<section css={jsmLoggedInCss}>
			<div css={heroSectionCss}>
				<div css={starCss}>
					<Picture
						image={{
							width: 88,
							height: 70,
							src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/g06k378gucflnfse7s88i0365h14j40v.webp',
							alt: '',
						}}
					/>
				</div>
				<h1 css={headingCss}>
					{userFirstName ? (
						<FormattedMessage
							id="JiraServiceManagementPageTemplate.loggedInHero.welcomeBackWithName"
							defaultMessage={`Welcome back, {name}.`}
							values={{
								name: <span css={nameUnderlineCss}>{userFirstName}</span>,
							}}
						/>
					) : (
						<FormattedMessage
							id="JiraServiceManagementPageTemplate.loggedInHero.welcomeBack"
							defaultMessage="Welcome back"
						/>
					)}
				</h1>
				<div css={subHeadingContainerCss}>
					<div css={subHeadingCss}>
						<FormattedMessage
							id="JiraServiceManagementPageTemplate.loggedInHero.pickUpWhereYouLeftOffInProduct"
							defaultMessage="Pick up where you left off in <span>{product}</span>"
							values={{
								product: productName,
								// eslint-disable-next-line react/no-unstable-nested-components
								span: (text: ReactNode) => (
									<span>
										<div css={inlineJSMLogoCss}>
											<LogoMark />
										</div>
										{text}
									</span>
								),
							}}
						/>
					</div>
					<a
						data-testid="createNewSiteAnchor"
						css={createNewSiteAnchorCss}
						href={createNewSiteHref}
						onClick={onCreateNewSiteClick}
					>
						<FormattedMessage
							id="WacSignupForm.createNewSite"
							defaultMessage="Create a new workspace"
						/>
					</a>
				</div>
				<div css={anchorContainer}>
					<div css={jsmSiteBoxCss}>
						<div css={siteInfoContainerCss}>
							<div css={sitenameIconCss}>
								<h2 className="h4" css={sitenameIconTextCss}>
									{siteInitials}
								</h2>
							</div>
							<div css={sitenameAndAvatarContainerCss}>
								<div css={tooltipWrapperCss}>
									<Tooltip content={siteName} position={'top-end'} mousePosition="top" tag={'span'}>
										<span css={siteNameSpanCss}>{siteName}</span>
									</Tooltip>
								</div>
								<div css={avatarContainerCss}>
									{userPicture && <AvatarFacepile max={4} iconSize={24} icons={[userPicture]} />}
								</div>
							</div>
						</div>
						<div css={goToProductButtonWrapperCss}>
							<Button
								data-testid="goToJsmButton"
								variant="primary_alt"
								href={siteUrl}
								size="large"
								onClick={onGoToProductClick}
								css={goToProductButtonCss}
							>
								<FormattedMessage {...goToProductMessage} />
							</Button>
						</div>
					</div>
				</div>
				{hasMultipleSites ? (
					<div css={differentSiteCss}>
						{/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
						<Link
							text={intl.formatMessage({
								id: 'JiraServiceManagementPageTemplate.loggedInHero.lookingForDifferentSite',
								defaultMessage: 'Looking for a different workspace?',
							})}
							href={existingSitesHref}
							type="primary"
							onClick={onDifferentSiteClick}
						/>
					</div>
				) : (
					<div css={linkPlaceholderCss} />
				)}
			</div>
		</section>
	);
};

export { LoggedInHeroSection };

export default LoggedInHeroSection;
